import React, { useEffect, useRef, useState } from "react"
import qs from "qs"
import algoliasearch from "algoliasearch/lite"
import {
  connectHits,
  connectInfiniteHits,
  connectSearchBox,
  connectStateResults,
  Configure,
  Index,
  InstantSearch,
} from "react-instantsearch-dom"
import { navigate, useStaticQuery, graphql } from "gatsby"
import { useDebounce } from "components/hooks"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import tw, { styled } from "twin.macro"

import {
  filterHit,
  buildSearchClient,
  pageSearchFilters,
  filterPageHits,
  getUserToken,
} from "utils/algolia"
import AppCard from "components/app-card"
import PageCard from "components/page-card"
import InsightsCard from "components/insights-card"
import IconSearch from "images/icons/search.inline.svg"
import IconClose from "images/icons/close.inline.svg"

const createURL = (state) => {
  const search = {}
  if (state.query) {
    search.query = state.query
  }
  const queryString = qs.stringify(search)
  if (queryString) {
    return `?${queryString}`
  } else {
    return ""
  }
}

const searchStateToUrl = (searchState) => {
  return searchState ? `/search/${createURL(searchState)}` : ""
}

const urlToSearchState = ({ search }) => qs.parse(search.slice(1))

const algoliaClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
)

const searchClient = buildSearchClient(algoliaClient)

const HitsTitle = connectStateResults(
  ({
    searchResults,
    expand,
    collapse,
    expanded = false,
    title,
    setCount,
    display = true,
  }) => {
    const hits = searchResults?.nbSortedHits || searchResults?.nbHits
    const displayCount = !hits || hits.length === 0 ? 1 : hits.length

    useEffect(() => {
      if (setCount) {
        setCount(hits)
      }
    }, [setCount, hits])

    if (display) {
      return (
        <div className="w-full flex flex-row mt-4 pt-8 pb-6">
          <h4>
            {title} ({displayCount ? displayCount : hits})
          </h4>
          {!!expand && !expanded && hits > 0 && (
            <span className="show-all text-w" onClick={expand}>
              Show All
            </span>
          )}
          {!!expand && !!expanded && (
            <span className="show-all text-w" onClick={collapse}>
              Show Less
            </span>
          )}
        </div>
      )
    } else return null
  }
)

const AppHits = connectHits(({ hits, settings }) => (
  <div className="mx-auto max-w-s m:max-w-m l:max-w-l">
    <div className="flex flex-wrap m:-mx-2 overflow-display">
      {hits.map((hit, idx) => (
        <AppCard app={hit} settings={settings} key={idx} />
      ))}
    </div>
  </div>
))

const PageHits = connectHits(({ hits, settings }) => (
  <div className="mx-auto max-w-s m:max-w-m l:max-w-l">
    <div className="flex flex-wrap m:-mx-2 overflow-display">
      {filterPageHits(hits).map((hit, idx) => (
        <PageCard page={hit} settings={settings} key={idx} />
      ))}
    </div>
  </div>
))

export const InsightsHits = connectInfiniteHits(
  ({ hits, infinite = false, hasMore, refineNext, settings }) => {
    const [ref, inView] = useInView()
    const [allowLoad, setAllowLoad] = useState(true)

    useEffect(() => {
      if (allowLoad && inView && hasMore) {
        setAllowLoad(false)
        setTimeout(() => setAllowLoad(true), 500)
        refineNext()
      }
    }, [allowLoad, hasMore, inView, refineNext])

    return (
      <>
        {hits.map((hit, idx) => {
          const post = filterHit(hit, settings)
          return <InsightsCard post={post} key={idx} search />
        })}
        {infinite && allowLoad && <div id="load-more" ref={ref} />}
      </>
    )
  }
)

export const SearchBox = connectSearchBox(
  ({ active, close, refine, autofocus, value, onChange }) => {
    const inputEl = useRef(null)
    const debouncedQuery = useDebounce(value, 300)

    useEffect(() => {
      autofocus && inputEl.current.focus()
    })

    useEffect(
      () => {
        if (debouncedQuery) {
          refine(debouncedQuery)
        }
      },
      [debouncedQuery, refine] // Only call effect if debounced search term changes
    )

    useEffect(() => active && active(value.length > 0), [value, active])

    function keyUp(event) {
      if (event.keyCode === 27) {
        if (close) {
          close()
        }
      }
    }

    return (
      <input
        id="search"
        className="flex flex-grow p-1 font-body text-xl text-grey-95 m:text-grey-10 bg-transparent outline-none"
        delay={500}
        onChange={onChange}
        onKeyUp={keyUp}
        name="search"
        placeholder="Type to search..."
        ref={inputEl}
        type="text"
        value={value}
      />
    )
  }
)

const StyledSearchBox = styled(SearchBox)`
  @media (min-width: 640px) {
    &::-webkit-search-cancel-button {
      position: relative;
      right: 20px;
      -webkit-appearance: none;
    }
  }
`

const SearchBar = styled.div`
  ${tw`flex flex-row mx-auto w-full items-center m:sticky m:top-0`}

  z-index: 100;
  @media (min-width: 640px) {
    height: 120px;
  }

  &.loaded {
    @media (min-width: 640px) {
      background-color: white;
    }
  }

  #icon-search {
    fill: #b3b3b3;

    @media (min-width: 640px) {
      height: 38px;
      width: 38px;
    }
  }

  .blue {
    path {
      fill: #3498db;
    }
  }

  #icon-close {
    path {
      fill: #000000;
    }
  }

  .clear-btn {
    ${tw`text-grey-65 border-grey-65 m:px-6 m-0 hidden m:block`}
    border-right: 2px solid;
    font-size: 17px;
    height: 24px;
    cursor: pointer;
  }
`

const HitsGradient = styled.div`
  @media (min-width: 640px) {
    position: sticky;
    top: 123px;
    left: 0;
    width: 100%;
    height: 24px;
    background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    z-index: 100;
  }
`

const SearchSpinner = connectStateResults(({ searching }) => {
  return (
    <div
      className="overflow-hidden w-full sticky"
      style={{
        top: 120,
        height: 3,
        backgroundColor: searching ? "rgb(27, 80, 115)" : "#3498db",
        zIndex: 100,
      }}
    >
      {searching && (
        <motion.div
          key="industry-selector-motion"
          animate={{ scaleX: 1, x: "100%" }}
          initial={{ scaleX: 0, x: 0 }}
          transition={{ duration: 2, repeatType: "loop", repeat: Infinity }}
          className="absolute"
          style={{
            backgroundColor: "#3498db",
            height: "3px",
            originX: 0,
            top: 0,
            width: "100%",
            zIndex: 100,
          }}
        ></motion.div>
      )}
    </div>
  )
})

const HitsBG = styled.div`
  ${tw`absolute left-0 right-0 top-0 mt-2 py-32 w-full`}

  h4 {
    ${tw`font-display font-bold text-white`}
    height: 14px;
    font-size: 16px;
    line-height: 14px;
    text-transform: uppercase;
  }

  .show-all {
    ${tw`font-display font-bold text-dodgeblue-light`}
    height: 10px;
    cursor: pointer;
    font-size: 10px;
    letter-spacing: 0;
    line-height: 16px;
    padding-left: 16px;
    text-transform: uppercase;
  }

  .expanded .show-all {
    display: none;
  }
`

export default ({ collapse, container, close, autofocus, location }) => {
  const urlState = urlToSearchState(location)
  const [searchState, setSearchState] = useState(urlState)
  const [showHits, setShowHits] = useState(false)
  const [query, setQuery] = useState(urlState.query || "")
  const [loaded, setLoaded] = useState(false)
  const [allApps, setAllApps] = useState(false)
  const [allPages, setAllPages] = useState(false)
  const [appHits, setAppHits] = useState()
  const [pageHits, setPageHits] = useState()
  const [insightsHits, setInsightsHits] = useState(0)
  const DEBOUNCE_TIME = 400
  const [debouncedSetState, setDebouncedSetState] = useState(null)
  const [focusValue, setFocusValue] = useState(autofocus)
  const updateUrl = /^\/search\/?$/.test(location.pathname)
  const { gcms: settings } = useStaticQuery(graphql`
    {
      gcms {
        insightsSearch: websiteSetting(where: { name: "Insights search" }) {
          jsonValue
        }
        titlesSearch: websiteSetting(where: { name: "Titles search" }) {
          jsonValue
        }
        platformsSearch: websiteSetting(where: { name: "Platforms search" }) {
          jsonValue
        }
        pagesSearch: websiteSetting(where: { name: "Pages search" }) {
          jsonValue
        }
      }
    }
  `)
  const insightsConfig = settings.insightsSearch.jsonValue
  const fallbackResult = {
    title: insightsConfig.fallbackTitle,
    excerpt: insightsConfig.fallbackExcerpt,
    path: insightsConfig.fallbackLinkTo,
    featuredImage: { url: insightsConfig.fallbackImage },
    linkCaption: insightsConfig.fallbackLinkCaption,
  }

  const onSearchStateChange = (updatedSearchState) => {
    if (updateUrl && updatedSearchState.query !== searchState.query) {
      clearTimeout(debouncedSetState)
      setDebouncedSetState(
        setTimeout(() => {
          const url = searchStateToUrl(updatedSearchState)
          navigate(url, { replace: true })
        }, DEBOUNCE_TIME)
      )
    }

    setSearchState(updatedSearchState)
  }

  function scrollToTop() {
    if (container && container.current) {
      container.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      })
    }
  }

  function expandApps(e) {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAllApps(true)
    scrollToTop()
  }

  function collapseApps(e) {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAllApps(false)
    scrollToTop()
  }

  function expandPages(e) {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAllPages(true)
    scrollToTop()
  }

  function collapsePages(e) {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAllPages(false)
    scrollToTop()
  }

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true)
    }, 200)
  })

  function exit(event) {
    if (!event || event.target === event.currentTarget) {
      setLoaded(false)
      setShowHits(false)
      close()
    }
  }

  function active(isActive) {
    if (collapse) {
      collapse(isActive)
    }
    setShowHits(isActive)
  }

  function clear() {
    onSearchStateChange({})
    setQuery("")
    setShowHits(false)
    clearCollapse()
    setFocusValue(true)
  }

  function onChange(event) {
    const value = event.currentTarget.value
    setQuery(value)
    active(value.length > 0)
    clearCollapse()
  }

  function clearCollapse() {
    setAllApps(false)
    setAllPages(false)
  }

  return (
    <InstantSearch
      indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}
      searchClient={searchClient}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
      searchState={searchState}
    >
      <div
        id="search-bg"
        onClick={exit}
        className="absolute top-0 left-0 w-full h-screen hidden m:block"
      />
      <SearchBar
        className={`pointer-events-auto ${showHits ? "active" : ""} ${
          loaded ? "loaded" : ""
        }`}
      >
        <div className={`p-1 mx-2 m:p-10 ${showHits ? "blue" : ""}`}>
          <IconSearch id="icon-search" />
        </div>
        <StyledSearchBox
          active={active}
          autofocus={focusValue}
          close={exit}
          onChange={onChange}
          value={query}
        />
        {showHits && (
          <div className="clear-btn" onClick={clear}>
            Clear
          </div>
        )}
        <div className="m:px-10 mx-2 m:pt-1 hidden m:block">
          <IconClose
            onClick={exit}
            style={{ cursor: "pointer" }}
            id="icon-close"
          />
        </div>
      </SearchBar>
      {!!loaded && <SearchSpinner />}
      <HitsGradient />
      {showHits && (
        <>
          <Configure distinct />
          <HitsBG onClick={exit}>
            <div className={appHits === 0 || allPages ? "hidden" : ""}>
              <Index indexName="Applications">
                <Configure hitsPerPage={allApps ? 100 : 4} />
                <div className="mx-auto max-w-s m:max-w-m l:max-w-l px-2 m:px-0">
                  <HitsTitle
                    title="Solutions"
                    collapse={collapseApps}
                    expand={expandApps}
                    expanded={allApps}
                    setCount={setAppHits}
                  />
                </div>
                <AppHits settings={settings} />
              </Index>
            </div>
            <div className={pageHits === 0 || allApps ? "hidden" : ""}>
              <Index indexName="PagesSorted">
                <Configure
                  hitsPerPage={allPages ? 100 : 4}
                  filters={pageSearchFilters()}
                />
                <div className="mx-auto max-w-s m:max-w-m l:max-w-l px-2 m:px-0">
                  <HitsTitle
                    title="Pages"
                    collapse={collapsePages}
                    expand={expandPages}
                    expanded={allPages}
                    setCount={setPageHits}
                  />
                </div>
                <PageHits settings={settings} />
              </Index>
            </div>
            <div className={allApps || allPages ? "hidden" : ""}>
              <div className="mx-auto max-w-s m:max-w-m l:max-w-l">
                <div className="flex flex-wrap m:-mx-2 overflow-display">
                  <Index indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}>
                    <Configure
                      hitsPerPage={20}
                      clickAnalytics
                      enablePersonalization={true}
                      userToken={getUserToken()}
                    />
                    <div className="w-full px-2">
                      <HitsTitle
                        title="Insights"
                        expanded={true}
                        expand={false}
                        setCount={setInsightsHits}
                      />
                    </div>
                    <InsightsHits infinite={true} settings={settings} />
                  </Index>
                  {insightsHits === 0 && (
                    <InsightsCard
                      post={fallbackResult}
                      key="insights-fallback"
                      search
                    />
                  )}
                </div>
              </div>
            </div>
          </HitsBG>
        </>
      )}
    </InstantSearch>
  )
}
